<template>
  <div v-if="!readonly || readOnlyValidation" :style="textStyle">
    <v-text-field
      class="verticalAlignment"
      v-if="showSpinner"
      :id="id"
      :aria-describedby="id"
      :aria-label="ariaLabel"
      :variant="readonly ? 'plain' : 'filled'"
      :readonly="readonly"
      v-model="computedValue"
      hide-details="auto"
      :required="isRequired"
      :disabled="isDisabled"
      @keypress="handler"
      @blur="convertToString"
      :rules="computedRules"
      type="number"
      min="0"
      step="any"
      :max="max"
      reverse
    >
      <template v-if="appendWith" v-slot:append> {{ appendWith }}</template>
    </v-text-field>
    <v-text-field
      v-else
      class="verticalAlignment"
      :id="id"
      :aria-describedby="id"
      :aria-label="ariaLabel"
      :variant="readonly ? 'plain' : 'filled'"
      :readonly="readonly"
      v-model="computedValue"
      hide-details="auto"
      :required="isRequired"
      :disabled="isDisabled"
      @keypress="handler"
      @blur="convertToString"
      @focus="
        {
          showCommas = false;
          isFieldFocused = true;
        }
      "
      :decimalPlaces="decimalPlaces"
      :rules="computedRules"
      :maxlength="maxlength"
      :min="min"
      :max="max"
      reverse
    >
      <template v-if="appendWith" v-slot:append>{{ appendWith }}</template>
      <template v-if="prependWithCurrencyIcon" v-slot:prepend-inner>
        <v-icon icon="fas fa-dollar-sign " />
      </template>
    </v-text-field>
  </div>
  <div v-else-if="(readonly || !readOnlyValidation) && !computedValue && showPlaceholder">
    <span>{{ "--" }}</span>
  </div>
  <div v-else :class="alignRight ? 'alignRight' : ''" :style="textStyle">
    <span v-if="prependWithCurrencyIcon">
      <v-icon icon="fas fa-dollar-sign" />
    </span>
    <span>
      {{ computedValue }}
    </span>
    <span v-if="appendWith">{{ appendWith }}</span>
  </div>
</template>
<script>
import { requiredRule } from "@/composables/validationRules";
import useFieldEntry from "@/composables/fieldEntry";
import { computed, ref } from "vue";
import { REGEX_PATTERNS } from "@/constants/RegexPatterns";

export default {
  name: "NumberField",
  props: {
    id: {},
    rules: { default: [] },
    readonly: { type: Boolean, default: false },
    modelValue: { default: "" },
    showSpinner: { type: Boolean, default: false },
    isRequired: { default: false },
    decimalPlaces: { default: null },
    maxlength: { default: 7 },
    maxValue: { default: null },
    min: { default: null },
    appendWith: { default: null },
    allowCommas: { type: Boolean, default: true },
    pairCode: { default: null },
    roundingFactor: { default: null },
    showComma: { type: Boolean, default: true },
    showPlaceholder: { type: Boolean, default: false },
    alignRight: { type: Boolean, default: false },
    readOnlyValidation: { type: Boolean, default: false },
    noNegativeDisplayed: { type: Boolean, default: false },
    showMax: { type: Boolean, default: true },
    allowNegativeInput: { type: Boolean, default: false },
    ariaLabel: { default: null },
    conversionTypes: { default: [] },
    textStyle: { default: "width: 50%" },
    isDisabled: { type: Boolean, default: false },
    prependWithCurrencyIcon: { type: Boolean, default: false },
  },
  setup(props, context) {
    const { isDecimalNumber, isNumber, isNumberWithNegative, isDecimalNumberWithNegative } = useFieldEntry();
    let max = ref(props.maxValue);
    let isFieldFocused = ref(false);

    let showCommas = ref();
    if (props.showComma) {
      showCommas.value = true;
    } else {
      showCommas.value = false;
    }

    //get the max value from max length, maxlength = 7 will return 9999999
    function setMaxValue() {
      let result = "";
      if (!max.value) {
        for (let i = 0; i < props.maxlength; i++) {
          result += "9";
        }
        max.value = Number.parseInt(result);
      }
    }
    setMaxValue();

    const valueInLimitsRule = (value) => {
      if (props.showMax) {
        if (Number.parseInt(value) >= props.min && Number.parseInt(value) <= max.value) {
          return true;
        } else if (value) {
          return `Valid range ${props.min} to ${max.value}`;
        }
      } else if (Number.parseInt(value) >= props.min) {
        return true;
      } else if (value) {
        return `Must be greater than ${props.min - 1}`;
      }
    };

    const getFloatValue = (value) => {
      //replace commas in the pasted value so parsefloat will work
      value = value?.toString()?.replaceAll(",", "");
      return Number.parseFloat(value) || Number.parseFloat(value) === 0 ? Number.parseFloat(value) : null;
    };

    let enteredValue = props.modelValue;

    const computedValue = computed({
      get: () => {
        // if field is not focused, return the value based on commas or decimals.
        let value = props.modelValue;

        if (isFieldFocused.value) {
          value = enteredValue;
        } else {
          enteredValue = value;
        }

        if (props.showSpinner) {
          return value;
        }
        if (showCommas.value) {
          //add commas
          return value
            ?.toString()
            ?.replace(REGEX_PATTERNS.NUMBER_WITH_COMMAS.FIRST_HALF, REGEX_PATTERNS.NUMBER_WITH_COMMAS.SECOND_HALF);
        } else {
          return value?.toString() || "";
        }
      },

      set: (value) => {
        enteredValue = value;
        //if user ends a number with period, don't update the model value until a number afer decimal is entered.
        if (value.toString().endsWith(".")) {
          //if .5 is entered set it to 0.5
          if (value.toString().length === 1) {
            value = 0;
          }
        }
        context.emit("update:modelValue", getFloatValue(value));
      },
    });

    const isValidDecimals = (value) => {
      if (props.decimalPlaces) {
        const afterDot = value.toString().split(".")[1];
        if (afterDot == undefined) {
          return true;
        } else {
          return afterDot.length <= props.decimalPlaces;
        }
      }
      return true;
    };

    const convertToString = (e) => {
      decimalHandler(e);
      isFieldFocused.value = false;
      if (props.showComma) {
        showCommas.value = true;
      }
      context.emit("update:blur");
    };

    const computedRules = computed(() => {
      let validationRules = [];
      if (props.rules && props.rules.length > 0) {
        validationRules = validationRules.concat(props.rules);
      }
      if (props.isRequired) {
        validationRules.push(requiredRule);
      }
      if (computedValue.value && (props.min || props.min == 0 || props.showSpinner)) {
        validationRules.push(valueInLimitsRule);
      }
      return validationRules;
    });

    function handler(e) {
      const isInteger = props.decimalPlaces == null;
      if (props.allowNegativeInput) {
        return isInteger ? isNumberWithNegative(e, props.allowCommas) : isDecimalNumberWithNegative(e);
      } else {
        return isInteger ? isNumber(e, props.allowCommas) : isDecimalNumber(e);
      }
    }

    const setDecimalLength = (value) => {
      if (props.decimalPlaces) {
        const multiplier = Math.pow(10, props.decimalPlaces);
        const [beforeDot, afterDot] = (Math.round(Number.parseFloat(value) * multiplier) / multiplier)
          .toFixed(props.decimalPlaces)
          .split(".");
        if (afterDot == undefined) {
          return beforeDot;
        } else {
          let afterDotTrimmed;

          if (afterDot.length > props.decimalPlaces) {
            afterDotTrimmed = afterDot.slice(0, props.decimalPlaces);
          } else {
            afterDotTrimmed = afterDot;
          }
          return beforeDot + "." + afterDotTrimmed;
        }
      } else {
        return value;
      }
    };

    function decimalHandler(e) {
      if (!isValidDecimals(e.target.value.toString())) {
        computedValue.value = setDecimalLength(e.target.value.toString());
      }
    }

    return {
      computedRules,
      isDecimalNumber,
      convertToString,
      isNumber,
      handler,
      computedValue,
      max,
      showCommas,
      isFieldFocused,
    };
  },
};
</script>

<style scoped lang="scss">
.verticalAlignment {
  align-items: center;
}
.alignRight {
  text-align: right;
}
</style>
